<template>
	<button
		:class="[
			'button',
			theme === 'outline'
				? 'button--outlined'
				: !theme
				? 'button--'
				: theme === 'primary'
				? 'button--primary'
				: theme === 'black'
				? 'button--black'
				: theme === 'blue'
				? 'button--blue'
				: theme === 'white'
				? 'button--white'
				: theme === 'secondary'
				? 'button--secondary'
				: theme === 'danger'
				? 'button--danger'
				: theme === 'text'
				? 'button--text'
				: '',
			block ? 'button--block' : '',
			loading ? 'button--loading' : '',
		]"
		:style="{ height: `${height}px` }"
	>
		<span class="loading-circle"></span>
		<div v-if="prependInner" class="prepend-inner-wrapper">
			<slot name="prepend-inner" class="prepend-inner" />
		</div>
		<span class="button__text">
			{{ $t(text) }}
		</span>
		<div v-if="appendInner" class="append-inner-wrapper">
			<slot name="append-inner" class="append-inner" />
		</div>
	</button>
</template>

<script setup>
import { defineProps } from 'vue'

defineProps({
	block: {
		type: Boolean,
		default: false,
	},
	height: {
		type: Number,
		default: 44,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	text: {
		required: true,
		type: String,
	},
	theme: {
		required: true,
		type: String,
		validator(value) {
			return ['primary', 'outline', 'secondary', 'black', 'text'].includes(value)
		},
	},
	appendInner: {
		type: Boolean,
		default: false,
	},
	prependInner: {
		type: Boolean,
		default: false,
	},
})
</script>

<style lang="scss">
.button {
	display: flex;
	text-align: center;
	align-items: center;
	justify-content: center;

	box-sizing: border-box;
	padding: 12px 20px;

	border-radius: 12px;
	background: var(--button-primary-default-color);
	&.ab-btn {
		// background-color: #A3978B;
		background-color: #364656;
	}
	transition: var(--base-transition);
	text-decoration: none;

	cursor: pointer;
}
.button[disabled] {
	cursor: default;
	pointer-events: none;
	user-select: none;
}

.button[disabled].button--,
.button[disabled].button--secondary,
.button[disabled].button--primary {
	background-color: #CBCBCB;
	opacity: 1 !important;
}

.button__text {
	white-space: nowrap;
	font-family: var(--text-font);
	font-weight: 400;
	font-style: normal;
	font-size: 16px;
	line-height: 20px;
	text-decoration: none;
	color: var(--button-primary-font-active-color);
}

.button.focused,
.button:hover,
.button:focus-visible {
	outline: none;
	background: var(--button-primary-hover-color);
	&.ab-btn {
		// background-color: rgb(163, 151, 139, .5);
		background-color: #AFB1B8;
	}
}

.button.button-disabled,
.button:disabled {
	opacity: 0.3;
}

.button--black {
	background: var(--button-primary-disabled-color);
}

.button--blue {
	background: #e8f1ff;
	.button__text {
		color: black;
	}
}

.button--block {
	width: 100%;
}

.button.button--loading {
	position: relative;
	pointer-events: none;
}

.button.button--loading .button__text {
	opacity: 0;
}

.button.button--loading .loading-circle::after {
	content: '';
	position: absolute;
	width: 24px;
	height: 24px;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	margin: auto;
	border: 2px solid transparent;
	// border-top-color: rgb(var(--v-theme-white100));
	// @apply border-t-white100;
	border-radius: 50%;
	animation: button-loading-spinner 1s ease infinite;
}

.button.button--loading.button--black .loading-circle::after,
.button.button--loading.button--outlined .loading-circle::after {
	// border-top-color: rgb(var(--v-theme-primary));
	// @apply border-t-primary;
}

@keyframes button-loading-spinner {
	from {
		transform: rotate(0turn);
	}

	to {
		transform: rotate(1turn);
	}
}

.button--outlined {
	background: transparent;
	border: var(--button-outlined-default-border);
}

.button--outlined .button__text {
	color: var(--button-outlined-font-active-color);
}

.button--outlined:hover,
.button--outlined:focus-visible {
	background: transparent;
	border: var(--button-outlined-hover-border);
}
.button--outlined.button-disabled:hover,
.button--outlined.button-disabled:focus-visible,
.button--outlined:disabled:hover,
.button--outlined:disabled:focus-visible {
	border: var(--button-outlined-default-border);
}

.button--outlined.button-disabled,
.button--outlined:disabled {
	opacity: 0.5;
}

.append-inner-wrapper,
.prepend-inner-wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
}
.append-inner,
.prepend-inner {
	width: 100%;
	height: 100%;
}
.append-inner-wrapper {
	margin: 0 0 0 12px;
}
.prepend-inner-wrapper {
	margin: 0 12px 0 0;
}

.button--secondary {
	background-color: var(--button-secondary-default-color);
	&.ab-btn {
		background-color: #D6C6B6;
	}
}

.button--secondary {
	.button__text {
		color: var(--button-secondary-font-active-color);
	}
	&.ab-btn .button__text {
		color: white;
	}
}

.button--secondary:hover,
.button--secondary:focus-visible {
	background-color: var(--button-secondary-hover-color);
	&.ab-btn {
		background-color: rgba(215, 199, 183, .5);
	}
}

.button--secondary.button-disabled,
.button--secondary:disabled {
	background-color: var(--button-secondary-disabled-color);
}

.button--secondary.button-disabled .button__text,
.button--secondary:disabled .button__text {
	color: var(--button-secondary-font-disabled-color);
}

.button--danger {
	background-color: var(--button-danger-default-color);
	&.ab-btn {
		background-color: #D6C6B6;
	}
}

.button--danger {
	.button__text {
		color: var(--button-danger-font-active-color);
	}
	&.ab-btn .button__text {
		color: white;
	}
}

.button--danger:hover,
.button--danger:focus-visible {
	background-color: var(--button-danger-hover-color);
	&.ab-btn {
		background-color: rgba(215, 199, 183, .5);
	}
}

.button--danger.button-disabled,
.button--danger:disabled {
	background-color: var(--button-danger-disabled-color);
}

.button--danger.button-disabled .button__text,
.button--danger:disabled .button__text {
	color: var(--button-danger-font-disabled-color);
}

.button--white {
	background-color: white;
}

.button--white {
	.button__text {
		color: #123785;
	}
}

.button--white:hover,
.button--white:focus-visible {
	background-color: color-mix(in srgb, white 93%, black);
	// background-color: color-mix(in srgb, #123785, white 10%);
}

.button--white:active {
	background-color: color-mix(in srgb, white 86%, black);
}

// .button--white.button-disabled,
// .button--white:disabled {
// 	background-color: var(--button-white-disabled-color);
// }

// .button--white.button-disabled .button__text,
// .button--white:disabled .button__text {
// 	color: var(--button-white-font-disabled-color);
// }

.button.button--text {
	border: 1px solid transparent;
	background: transparent;
}
.button--text .button__text {
	color: var(--text-primary-color);
}
.button.button--text:hover {
	border: var(--button-outlined-default-border);
	background: transparent;
}

</style>
